import React, { useEffect, useState } from 'react';

import { useAdContext } from '../../../../store/contexts/ad';
import { useAd } from '../../../../store/hooks/useAd';

import { getOptionsIds, optionalsFactory } from './utils';

import { ListItem, SearchInput, Title } from '@icarros/ids-react';

import {
  CATEGORY_COMFORT,
  CATEGORY_OTHER,
  CATEGORY_SEATS,
  CATEGORY_SECURITY,
  CATEGORY_SOUND,
  CATEGORY_WINDOWS
} from './constants';

import { Loading } from '../../../../components/Loading';
import { OptionalsList } from './OptionalsList';
import { getFilteredContent } from '../../../../utils/getFilteredContent';
import { ResultsNotFound } from '../../../../components/ResultsNotFound';
import { SaveFormContent } from '../SaveFormContent';

import { ListItemModifier } from './style';
import { OpenEditTitleWrapper, OpenEditWrapper } from '../style';
import { useQuery } from '@tanstack/react-query';
import { getOptionals } from '../../../../services/Edits.service';
import { GET_OPTIONALS } from '../../../../queries';

type SelectedBoxes = {
  security: number[] | undefined;
  comfort: number[] | undefined;
  sound: number[] | undefined;
  seatsAndWindows: number[] | undefined;
  other: number[] | undefined;
};

interface FilterReducer {
  nome: string;
  id: number;
  categoria: string;
}

export function OptionalsForm(): JSX.Element {
  const { setCurrentContent, loading } = useAd();
  const { currentAd, setValueIsDifferentFromCurrent } = useAdContext();
  const { opcionais } = currentAd?.veiculo || {};
  const [searchField, setSearchField] = useState('');
  const [selectedBoxes, setSelectedBoxes] = useState<SelectedBoxes>({
    security: undefined,
    comfort: undefined,
    sound: undefined,
    seatsAndWindows: undefined,
    other: undefined
  });

  const { data, isInitialLoading } = useQuery({
    queryKey: [GET_OPTIONALS],
    queryFn: () => getOptionals()
  });

  const [selectedBoxesInitialLength, setSelectedBoxesInitialLength] = useState(0);

  useEffect(() => {
    setSelectedBoxes({
      security: getOptionsIds(CATEGORY_SECURITY, opcionais),
      comfort: getOptionsIds(CATEGORY_COMFORT, opcionais),
      sound: getOptionsIds(CATEGORY_SOUND, opcionais),
      seatsAndWindows: [
        ...getOptionsIds(CATEGORY_SEATS, opcionais),
        ...getOptionsIds(CATEGORY_WINDOWS, opcionais)
      ],
      other: getOptionsIds(CATEGORY_OTHER, opcionais)
    });
    setSelectedBoxesInitialLength(opcionais ? opcionais.length : 0);
  }, [opcionais]);

  const flatSelectedBoxes = Object.values(selectedBoxes).flat();

  const isEqualToInitialState =
    selectedBoxesInitialLength === flatSelectedBoxes.length &&
    flatSelectedBoxes.every(item => opcionais?.find(itemOptional => itemOptional.id === item));

  const foundOptionals = searchField !== '' && data ? getFilteredContent(data, searchField) : data;

  useEffect(() => {
    setValueIsDifferentFromCurrent(!isEqualToInitialState);
  }, [isEqualToInitialState, setValueIsDifferentFromCurrent]);

  const securityOptionals = optionalsFactory(CATEGORY_SECURITY, foundOptionals);
  const comfortOptionals = optionalsFactory(CATEGORY_COMFORT, foundOptionals);
  const soundOptionals = optionalsFactory(CATEGORY_SOUND, foundOptionals);
  const seatsOptionals = optionalsFactory(CATEGORY_SEATS, foundOptionals);
  const windowsOptionals = optionalsFactory(CATEGORY_WINDOWS, foundOptionals);
  const otherOptionals = optionalsFactory(CATEGORY_OTHER, foundOptionals);
  const seatsAndWindowsOptionals =
    seatsOptionals && windowsOptionals
      ? [...seatsOptionals, ...windowsOptionals].sort((a, b) => (a.id > b.id ? 1 : -1))
      : [];

  return (
    <OpenEditWrapper>
      <OpenEditTitleWrapper>
        <Title size="overline">gerenciar opcionais</Title>
        <Title variant="default" contrast="light">
          selecione os opcionais do seu carro
        </Title>
        <SearchInput
          contrast="default"
          placeholder="buscar opcionais"
          onChange={event => setSearchField(event.target.value)}
        />
      </OpenEditTitleWrapper>
      {isInitialLoading && <Loading />}
      {!isInitialLoading && (!foundOptionals || foundOptionals.length === 0) && <ResultsNotFound />}

      {data && foundOptionals && foundOptionals.length > 0 ? (
        <>
          <ListItemModifier>
            <ListItem
              label="opcionais selecionados"
              descript={flatSelectedBoxes && String(flatSelectedBoxes.length)}
              variant="simple"
            />
          </ListItemModifier>
          {comfortOptionals && (
            <OptionalsList
              label="conforto"
              optionalsList={comfortOptionals}
              selectedType={selectedBoxes.comfort}
              selectedBoxesOption="comfort"
              setSelectedBoxes={setSelectedBoxes}
              searchQuerySize={searchField.length}
              shouldStartOpen={!opcionais}
            />
          )}
          {securityOptionals && (
            <OptionalsList
              label="segurança"
              optionalsList={securityOptionals}
              selectedType={selectedBoxes.security}
              selectedBoxesOption="security"
              setSelectedBoxes={setSelectedBoxes}
              searchQuerySize={searchField.length}
              shouldStartOpen={!opcionais}
            />
          )}
          {seatsOptionals && windowsOptionals && (
            <OptionalsList
              label="bancos e janelas"
              optionalsList={seatsAndWindowsOptionals}
              selectedType={selectedBoxes.seatsAndWindows}
              selectedBoxesOption="seatsAndWindows"
              setSelectedBoxes={setSelectedBoxes}
              searchQuerySize={searchField.length}
              shouldStartOpen={!opcionais}
            />
          )}
          {soundOptionals && (
            <OptionalsList
              label="sistema de som"
              optionalsList={soundOptionals}
              selectedType={selectedBoxes.sound}
              selectedBoxesOption="sound"
              setSelectedBoxes={setSelectedBoxes}
              searchQuerySize={searchField.length}
              shouldStartOpen={!opcionais}
            />
          )}
          {otherOptionals && (
            <OptionalsList
              label="outros"
              optionalsList={otherOptionals}
              selectedType={selectedBoxes.other}
              selectedBoxesOption="other"
              setSelectedBoxes={setSelectedBoxes}
              searchQuerySize={searchField.length}
              shouldStartOpen={!opcionais}
            />
          )}
        </>
      ) : (
        <></>
      )}
      {!(isInitialLoading && isEqualToInitialState) && (
        <SaveFormContent
          loading={loading}
          isDisabled={isEqualToInitialState}
          handleClick={() => {
            const selectedBoxesArray = Object.values(selectedBoxes).flat();
            const selectedOptionals = selectedBoxesArray.reduce<FilterReducer[] | undefined>(
              (acc, item) => {
                if (data) {
                  const result = data.find(optional => optional.id === item);
                  if (acc && result) {
                    return [...acc, result];
                  }
                }
                return acc;
              },
              [] as FilterReducer[] | undefined
            );
            if (!selectedOptionals) return null;

            setCurrentContent({
              veiculo: {
                opcionais: selectedOptionals
              }
            });
          }}
        />
      )}
    </OpenEditWrapper>
  );
}
